import React, { useEffect, useState } from "react";
import { Plus, Target, Check, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", target_value: 0, current_value: 0, unit: "lbs", deadline: "" });

  const load = () => api.get("/goals").then(({ data }) => setGoals(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Goal title required");
    try {
      await api.post("/goals", {
        ...form,
        target_value: Number(form.target_value) || 0,
        current_value: Number(form.current_value) || 0,
        deadline: form.deadline || null,
      });
      toast.success("Goal set.");
      setOpen(false);
      setForm({ title: "", target_value: 0, current_value: 0, unit: "lbs", deadline: "" });
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const updateProgress = async (g, delta) => {
    const newVal = Math.max(0, g.current_value + delta);
    await api.patch(`/goals/${g.goal_id}`, { current_value: newVal });
    load();
  };

  const markDone = async (g) => {
    await api.patch(`/goals/${g.goal_id}`, { status: "completed", current_value: g.target_value });
    toast.success("Goal crushed.");
    load();
  };

  const remove = async (id) => {
    await api.delete(`/goals/${id}`);
    load();
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="goals-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-tiny text-brand-accent mb-2">// MISSION TARGETS</div>
          <h1 className="font-heading text-4xl sm:text-5xl">GOALS</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-kinetic px-5 py-3 text-xs label-tiny tracking-widest flex items-center gap-2" data-testid="add-goal-button">
              <Plus size={14} weight="bold" /> NEW GOAL
            </button>
          </DialogTrigger>
          <DialogContent className="bg-surface-DEFAULT border-white/10 text-white max-w-md" data-testid="goal-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">SET GOAL</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="label-tiny block mb-2">TITLE</label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Bench 225lbs" className="bg-surface-elevated border-white/10" data-testid="goal-title-input" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="label-tiny block mb-2">CURRENT</label>
                  <Input type="number" value={form.current_value} onChange={(e) => setForm({ ...form, current_value: e.target.value })} className="bg-surface-elevated border-white/10" data-testid="goal-current-input" />
                </div>
                <div>
                  <label className="label-tiny block mb-2">TARGET</label>
                  <Input type="number" value={form.target_value} onChange={(e) => setForm({ ...form, target_value: e.target.value })} className="bg-surface-elevated border-white/10" data-testid="goal-target-input" />
                </div>
                <div>
                  <label className="label-tiny block mb-2">UNIT</label>
                  <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="bg-surface-elevated border-white/10" data-testid="goal-unit-input" />
                </div>
              </div>
              <div>
                <label className="label-tiny block mb-2">DEADLINE (OPTIONAL)</label>
                <Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="bg-surface-elevated border-white/10" data-testid="goal-deadline-input" />
              </div>
              <button onClick={save} className="btn-kinetic w-full py-3 text-xs label-tiny tracking-widest" data-testid="save-goal-button">
                LOCK IN GOAL
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {goals.length === 0 ? (
        <div className="tactical-card p-12 text-center" data-testid="goals-empty">
          <Target size={36} weight="duotone" className="text-brand-primary mx-auto mb-4" />
          <h3 className="font-heading text-xl mb-2">NO ACTIVE GOALS</h3>
          <p className="text-sm text-text-secondary">Lock in your first target. Make it specific. Make it real.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="goals-list">
          {goals.map((g) => {
            const pct = g.target_value > 0 ? Math.min(100, (g.current_value / g.target_value) * 100) : 0;
            const isDone = g.status === "completed";
            return (
              <div key={g.goal_id} className={`tactical-card p-6 ${isDone ? "active" : ""}`} data-testid={`goal-card-${g.goal_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-heading text-xl">{g.title}</h3>
                    {g.deadline && <div className="label-tiny mt-1">DUE {new Date(g.deadline).toLocaleDateString()}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    {!isDone && (
                      <button onClick={() => markDone(g)} className="text-green-400 hover:text-green-300" data-testid={`complete-goal-${g.goal_id}`}>
                        <Check size={18} weight="bold" />
                      </button>
                    )}
                    <button onClick={() => remove(g.goal_id)} className="text-text-secondary hover:text-red-400" data-testid={`delete-goal-${g.goal_id}`}>
                      <Trash size={14} />
                    </button>
                  </div>
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="font-heading text-3xl tabular-nums text-brand-accent">{g.current_value}</span>
                  <span className="text-text-secondary">/ {g.target_value} {g.unit}</span>
                  {isDone && <span className="ml-auto label-tiny text-green-400">COMPLETE</span>}
                </div>
                <Progress value={pct} className="h-2 bg-surface-elevated" data-testid={`goal-progress-${g.goal_id}`} />
                {!isDone && (
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => updateProgress(g, -1)} className="border border-white/10 hover:border-white/40 px-3 py-1 text-xs label-tiny" data-testid={`goal-decrement-${g.goal_id}`}>-1</button>
                    <button onClick={() => updateProgress(g, 1)} className="border border-white/10 hover:border-white/40 px-3 py-1 text-xs label-tiny" data-testid={`goal-increment-${g.goal_id}`}>+1</button>
                    <button onClick={() => updateProgress(g, 5)} className="border border-white/10 hover:border-white/40 px-3 py-1 text-xs label-tiny" data-testid={`goal-increment5-${g.goal_id}`}>+5</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
